import BottomSheetModal from '@/components/modules/modals/BottomSheetModal';
import { _t } from '@/locale';
import { useEffect, useState } from 'react';
import SelectEmployeeList from '../views/SelectEmployeeList';
import { EmployeeInfoContent } from './EmployeeInfoModal';
import type { SelectEmployeeSheetProps } from './SelectEmployeeSheet.types';

/**
 * The employee list opened straight from the SelectionsBar's employee link. One view and no
 * stack behind it, so the back arrow appears only for the (i) overlay — unlike the same list
 * inside SelectionDrawer, which is reached from the summary.
 *
 * A pick closes the sheet: opened straight from the bar's employee link with no summary to
 * return to, so applying the pick is the whole job.
 */
const SelectEmployeeSheet = ({ isOpen, onClose, employeeList }: SelectEmployeeSheetProps) => {
  const [infoEmployeeId, setInfoEmployeeId] = useState<number | undefined>(undefined);
  const infoEmployee = employeeList.employees.find((employee) => employee.id === infoEmployeeId);

  // Reopening starts on the list, never on the presentation text it was left on.
  useEffect(() => {
    if (isOpen) setInfoEmployeeId(undefined);
  }, [isOpen]);

  const onPick = (employeeId: number) => {
    employeeList.onPick(employeeId);
    onClose();
  };

  return (
    <BottomSheetModal
      isOpen={isOpen}
      onClose={onClose}
      title={infoEmployee?.name ?? _t('features.bookingSelection.SelectEmployeeList.title')}
      onBack={infoEmployee ? () => setInfoEmployeeId(undefined) : undefined}
      backLabel={_t('back')}>
      {/* The (i) is an overlay inside this sheet, not a second sheet on top of it, and one grid
          cell keeps the covered list mounted with its scroll offset. */}
      <div className="grid min-h-0 flex-1">
        {/* inert: no focus or clicks under the overlay. aria-hidden stays for role queries in tests
            (jsdom ignores inert) and pre-inert browsers. React 18 only forwards inert as a string. */}
        <div
          inert={infoEmployee ? '' : undefined}
          aria-hidden={Boolean(infoEmployee) || undefined}
          className="bg-surface_mp px-md py-md col-start-1 row-start-1 overflow-y-auto">
          <SelectEmployeeList
            {...employeeList}
            onPick={onPick}
            indicator="select"
            onOpenEmployeeInfo={setInfoEmployeeId}
          />
        </div>
        {infoEmployee && (
          // z-10: the list's drop-shadowed cards form stacking contexts that would paint above.
          <div
            data-cy="sheet-employee-info"
            className="bg-surface_mp px-md py-md relative z-10 col-start-1 row-start-1 overflow-y-auto">
            <EmployeeInfoContent employee={infoEmployee} />
          </div>
        )}
      </div>
    </BottomSheetModal>
  );
};

export default SelectEmployeeSheet;
